/**
 * 商品画像枠の並べ替えモード (Amazon / マニュアル共通) の純関数。
 *
 * 並べ替えは「枠の並び (配列)」を編集し、完了時に元の並びと比べて変わった枠だけを保存する。
 * 配列の位置 i が枠 (Amazon: pt01..pt08 / マニュアル: main, sub0..sub7) に対応し、
 * 各要素の key は「その画像が元々あった枠」を表す (画像の名前を画像と一緒に動かすために使う)。
 */

export type ReorderItem = {
  /** 元々あった枠のキー (位置が変わっても付いて回る) */
  key: string;
  url: string | null;
  /** 画像の名前 (画像内容の説明。画像と一緒に動く) */
  name?: string | null;
  /** 保存時にそのまま戻す付随データ (マニュアルの width / height 等) */
  meta?: Record<string, unknown>;
};

/** 並びが変わったか (位置ごとに元の枠キーが同じなら未変更) */
export function isSameOrder(original: ReorderItem[], reordered: ReorderItem[]): boolean {
  return original.length === reordered.length && original.every((item, i) => item.key === reordered[i].key);
}

/**
 * Amazon 商品: 並べ替え後の配列から POST /api/amazon/products/<asin>/images の本文を組み立てる。
 * slotKeys[i] が位置 i の枠。URL が変わった枠だけを images に入れ (空になった枠は '' = 削除)、
 * 別の枠から来た画像には moved_from ({先: 元}) を付けて名前を一緒に動かす。
 */
export function buildAmazonReorderPayload(
  slotKeys: string[],
  original: ReorderItem[],
  reordered: ReorderItem[],
): { images: Record<string, string>; movedFrom: Record<string, string>; emptied: string[] } {
  const images: Record<string, string> = {};
  const movedFrom: Record<string, string> = {};
  const emptied: string[] = [];
  slotKeys.forEach((slotKey, i) => {
    const before = original[i]?.url || '';
    const item = reordered[i];
    const after = item?.url || '';
    if (before === after) return;
    images[slotKey] = after;
    if (!after) {
      emptied.push(slotKey);
    } else if (item.key !== slotKey) {
      movedFrom[slotKey] = item.key;
    }
  });
  return { images, movedFrom, emptied };
}

export type ManualImage = { url: string; name?: string; width?: number; height?: number; original_path?: string };

/**
 * マニュアル商品: [main, sub0..sub7] の並びから PUT /api/products/<id> の画像フィールドを組み立てる。
 * 名前は要素に内包されているので画像と一緒に動く。末尾の空き枠は落とし、途中の空き枠は
 * {url: ''} で枠位置を保つ。枠より後ろの要素 (extra) はそのまま残す。
 * ベクター原本 (SVG) のパスは meta.original_path として枠間を動き、メインに来たら列
 * (main_image_original_path)、追加画像に来たら要素のキー (original_path) に戻す
 * (docs/features/product-image-vector-original-plan.md §3.7)。
 */
export function buildManualReorderPayload(
  reordered: ReorderItem[],
  extra: ManualImage[] = [],
): { main_image_url: string; main_image_name: string | null; main_image_original_path: string | null; additional_images: ManualImage[] } {
  const [main, ...subs] = reordered;
  let lastFilled = -1;
  subs.forEach((item, i) => { if (item?.url) lastFilled = i; });
  const toImage = (item: ReorderItem): ManualImage => ({
    ...(item.meta as Partial<ManualImage> | undefined),
    url: item.url || '',
    ...(item.name ? { name: item.name } : {}),
  });
  const mainOriginal = typeof main?.meta?.original_path === 'string' ? (main.meta.original_path as string) : null;
  return {
    main_image_url: main?.url || '',
    main_image_name: main?.url ? main.name || null : null,
    main_image_original_path: main?.url ? mainOriginal : null,
    additional_images: [
      ...subs.slice(0, lastFilled + 1).map((item) => (item?.url ? toImage(item) : { url: '' })),
      ...extra,
    ],
  };
}
